import React from 'react';
import AuthService, {
    formatError,
    saveTokenInLocalStorage,
    removeTokenFromLocalStorage,
    runLogoutTimer,
    // Legacy functions for backward compatibility
    login,
    signUp
} from '../../services/AuthService';
import { handleSuccessNotification, handleErrorNotification } from '../utils/notificationUtils';


export const SIGNUP_CONFIRMED_ACTION = '[signup action] confirmed signup';
export const SIGNUP_FAILED_ACTION = '[signup action] failed signup';
export const LOGIN_CONFIRMED_ACTION = '[login action] confirmed login';
export const LOGIN_FAILED_ACTION = '[login action] failed login';
export const LOADING_TOGGLE_ACTION = '[Loading action] toggle loading';
export const LOGOUT_ACTION = '[Logout action] logout action';



export function signupAction(email, password, navigate) {
    return async (dispatch) => {
        dispatch(loadingToggleAction(true));
        try {
            const response = await AuthService.register({ email, password });
            if (response.success) {
                saveTokenInLocalStorage(response.data);
                
                // First dispatch the signup confirmed action to update state
                dispatch(confirmedSignupAction(response.data));
                
                // Then set up the logout timer
                runLogoutTimer(
                    dispatch,
                    24 * 60 * 60 * 1000, // 24 hours
                    navigate
                );
                
                // Show success message
                handleSuccessNotification(response, "Account created successfully! Welcome!");
                
                // Navigate after state is updated
                setTimeout(() => {
                    navigate('/dashboard');
                }, 100); // Small delay to ensure state update
            } else {
                dispatch(signupFailedAction(response.message));
                handleErrorNotification(response, "Registration failed");
            }
        } catch (error) {
            const errorMessage = formatError(error.response?.data || error);
            dispatch(signupFailedAction(errorMessage));
            handleErrorNotification(error, "Registration failed");
        } finally {
            dispatch(loadingToggleAction(false));
        }
    };
}

export function Logout(navigate) {
    return async (dispatch) => {
        try {
            // Call logout API
            await AuthService.logout();
        } catch (error) {
            console.error('Logout API call failed:', error);
        } finally {
            // Always clean up local storage and dispatch action
            removeTokenFromLocalStorage();
            dispatch({
                type: LOGOUT_ACTION,
            });
            navigate('/login');
        }
    };
}

export function loginAction(identifier, password, navigate) {
    return async (dispatch) => {
        dispatch(loadingToggleAction(true));
        
        try {
            const response = await AuthService.login(identifier, password);
            
            // Handle requiresPasswordChange (403 response)
            if (response.requiresPasswordChange) {
                // Store temporary token if provided for setup-password endpoint
                if (response.user) {
                    const tempData = {
                        user: response.user,
                        accessToken: null, // No token yet until password is set
                        requiresPasswordChange: true
                    };
                    localStorage.setItem('tempUserData', JSON.stringify(tempData));
                }

                // Navigate to setup password page without showing the error notification
                setTimeout(() => {
                    navigate('/setup-password');
                }, 500);

                dispatch(loadingToggleAction(false));
                return;
            }
            
            if (response.success) {
                saveTokenInLocalStorage(response.data);
                dispatch(loginConfirmedAction(response.data));
                runLogoutTimer(
                    dispatch,
                    24 * 60 * 60 * 1000, // 24 hours
                    navigate
                );
                setTimeout(() => {
                    navigate('/dashboard');
                }, 100); // Small delay to ensure state update
                
            } else {
                // Handle API response with success: false
                dispatch(loginFailedAction(response.message));
                formatError(response); // Only call formatError once for API response
            }
            
        } catch (error) {
            // Handle network/server errors
            console.error('Login error:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Network error occurred';
            dispatch(loginFailedAction(errorMessage));
            formatError({ message: errorMessage }); // Call formatError once for network errors
            
        } finally {
            // Always ensure loading is turned off
            setTimeout(() => {
                dispatch(loadingToggleAction(false));
            }, 50); // Small delay to prevent state conflicts
        }
    };
}

export function loginUsingDomainAction(website, identifier, password, navigate) {
    return async (dispatch) => {
        dispatch(loadingToggleAction(true));
        try {
            const response = await AuthService.loginUsingDomain(website, identifier, password);
            if (response.requiresPasswordChange) {
                if (response.user) {
                    const tempData = {
                        user: response.user,
                        accessToken: null,
                        requiresPasswordChange: true
                    };
                    localStorage.setItem('tempUserData', JSON.stringify(tempData));
                }

                // Navigate to setup password page without showing the error notification
                setTimeout(() => {
                    navigate('/setup-password');
                }, 500);
                dispatch(loadingToggleAction(false));
                return;
            }

            if (response.success) {
                const payload = { ...response.data, website };
                saveTokenInLocalStorage(payload);
                dispatch(loginConfirmedAction(payload));
                runLogoutTimer(
                    dispatch,
                    24 * 60 * 60 * 1000,
                    navigate
                );
                setTimeout(() => {
                    navigate('/dashboard');
                }, 100);
            } else {
                dispatch(loginFailedAction(response.message));
                formatError(response);
            }
        } catch (error) {
            console.error('loginUsingDomainAction error:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Network error occurred';
            dispatch(loginFailedAction(errorMessage));
            formatError({ message: errorMessage });
        } finally {
            setTimeout(() => {
                dispatch(loadingToggleAction(false));
            }, 50);
        }
    };
}

export function loginFailedAction(data) {
    return {
        type: LOGIN_FAILED_ACTION,
        payload: data,
    };
}

export function loginConfirmedAction(data) {
    return {
        type: LOGIN_CONFIRMED_ACTION,
        payload: data,
    };
}

export function confirmedSignupAction(payload) {
    return {
        type: SIGNUP_CONFIRMED_ACTION,
        payload,
    };
}

export function signupFailedAction(message) {
    return {
        type: SIGNUP_FAILED_ACTION,
        payload: message,
    };
}

export function loadingToggleAction(status) {
    return {
        type: LOADING_TOGGLE_ACTION,
        payload: status,
    };
}
